import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { Money } from "../../../../components/ui";
import { FIT_VERDICT_LABEL } from "../scheduledOvertimeText";

// Report 70: how a pay agreement relates to the employee's weekly schedule — decision support only. Nothing here changes
// a salary or a rate; the words come from the server (days, never amounts), the amounts only when the reader may see pay.
// Hardening (P1): the verdict, then each finding with its next step; the paid-day arithmetic and its assumptions stay in
// the details, one click away.

const TONE = { ok: "text-foreground", notice: "text-warning", review: "text-warning" };
const ORDER = { review: 0, notice: 1, ok: 2 };

export default function ScheduleFitNote({ fit, loading = false, className = "" }) {
  if (loading) return <p className={`text-body-sm text-tertiary ${className}`} aria-live="polite">Checking the schedule…</p>;
  if (!fit) return null;
  const alternatives = fit.suggestion?.calendar?.alternatives || [];
  const suggested = fit.money?.suggestedDailyRates || [];
  const checks = [...(fit.checks || [])].sort((a, b) => (ORDER[a.level] ?? 3) - (ORDER[b.level] ?? 3));
  const Icon = fit.verdict === "ok" ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`space-y-2 text-body-sm ${className}`} aria-live="polite">
      <div>
        <p className={`flex items-center gap-1.5 font-medium ${TONE[fit.verdict] || "text-secondary"}`}>
          <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
          {FIT_VERDICT_LABEL[fit.verdict] || fit.verdict}
        </p>
        {fit.pattern ? <p className="text-meta text-secondary">Works {fit.pattern.label} · {fit.pattern.workDaysPerWeek} days a week</p> : null}
        <p className="text-meta text-tertiary">Compares the pay basis with the work days only; the rest of the pay record and payroll are checked separately.</p>
      </div>
      <ul className="space-y-2">
        {checks.map((check) => (
          <li key={check.code} className={check.level === "ok" ? "text-secondary" : "text-foreground"}>
            <p>{check.message}</p>
            {check.action ? (
              <p className="mt-0.5 text-secondary"><span className="font-medium text-foreground">Next step:</span> {check.action}</p>
            ) : null}
          </li>
        ))}
      </ul>
      {alternatives.length && fit.payKind === "salary" ? (
        <details className="group rounded-control border border-subtle bg-surface px-3 py-2 text-meta text-secondary">
          <summary className="cursor-pointer font-medium text-foreground">How the paid days a year are worked out</summary>
          <p className="mt-1">{fit.suggestion.label}. {fit.suggestion.published ? "" : "Suggested rates are not the agreed rate."}</p>
          <ul className="mt-2 space-y-1.5">
            {fit.suggestion.handbookStyle ? (
              <li>
                <span className="tabular-nums text-foreground">{fit.suggestion.handbookStyle.specialPaid}</span> days with special days paid,{" "}
                <span className="tabular-nums text-foreground">{fit.suggestion.handbookStyle.specialUnpaid}</span> without (Handbook arithmetic)
              </li>
            ) : null}
            {alternatives.map((item) => {
              const rate = suggested.find((entry) => entry.key === item.key);
              return (
                <li key={item.key}>
                  <span className="tabular-nums text-foreground">{item.days}</span> days · {item.label}
                  {rate?.dailyRateCents ? <> · suggested <Money value={rate.dailyRateCents} /> a day</> : null}
                  <span className="block text-tertiary">Assumes: {item.assumption}</span>
                </li>
              );
            })}
            {fit.suggestion.calendarNote ? <li>{fit.suggestion.calendarNote}</li> : null}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
